import { React, memo, useState, useEffect } from "react";
import classes from "./StockControl.module.css";
import Select from "react-select";
import StockTable from "./StockTable/StockTable";
import { Link } from "react-router-dom";
import axiosInstance from "../../Configuration/axiosInstance";

/*Separate the input and the Picker Components to stop re-render on every key stroke*/
const HeaderComponent = ({
  productHandler,
  productOptions,
  prodID,
  setSearchTerm,
  setDataChanged,
  products,
  clearFilters,
  setClearFilters,
  searchTerm,
  setSortOrder,
}) => {
  const handleClick = () => {
    setSearchTerm(prodID);
    setClearFilters(true);
    setDataChanged((prevState) => !prevState);
  };

  const handleFilter = () => {
    setSearchTerm(null);
    setClearFilters(false);
    setDataChanged((prevState) => !prevState);
  };

  return (
    <div className={classes.headerContainer}>
      <div className={classes.titleSection}>
        <h1 className={classes.pageTitle}>Stock Control</h1>
      </div>

      <div className={classes.controlsContainer}>
        <div className={classes.searchSection}>
          <div className={classes.inputGroup}>
            <label className={classes.inputLabel}>Search by Product</label>
            <Select
              className={classes.reactSelect}
              classNamePrefix="react-select"
              name="product"
              options={productOptions}
              onChange={productHandler}
              placeholder="Select a product..."
              isClearable
              isSearchable
            />
          </div>

          <button
            className={classes.searchBtn}
            onClick={handleClick}
            disabled={!prodID}
          >
            <span className={classes.buttonIcon}>🔍</span>
            Search
          </button>
        </div>

        <div className={classes.actionsSection}>
          <div className={classes.inputGroup}>
            <label className={classes.inputLabel}>Sort by Stock</label>
            <select
              className={classes.sortSelect}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="">Default Order</option>
              <option value="asc">Low to High</option>
              <option value="desc">High to Low</option>
            </select>
          </div>

          <Link to="/stockControl/newStock" className={classes.addStockBtn}>
            <span className={classes.buttonIcon}>➕</span>
            Add New Stock
          </Link>
        </div>
      </div>

      {/* Active Filters Display */}
      {clearFilters && searchTerm && (
        <div className={classes.activeFilters}>
          <div className={classes.filterChip}>
            <span className={classes.filterIcon}>🔍</span>
            <span className={classes.filterText}>
              {products
                .filter((i) => i._id === searchTerm)
                .map((i) => i.productName)}
            </span>
            <button
              className={classes.filterRemove}
              onClick={handleFilter}
              title="Clear filter"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const StockControl = () => {
  const [allStock, setAllStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataChanged, setDataChanged] = useState(false);
  const [searchTerm, setSearchTerm] = useState(null);
  const [products, setProducts] = useState([]);
  const [prodID, setProdID] = useState(null);
  const [warehouses, setWareHouses] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState("");

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`stockRoute?searchTerm=${searchTerm}`).then((res) => {
      let data = res.data.data;

      // 1. Compute total stock per item
      data = data.map((item) => {
        const totalQuantity = item.warehouses.reduce(
          (sum, warehouse) => sum + (warehouse.quantity || 0),
          0
        );
        return { ...item, totalQuantity };
      });

      // 2. Sort based on totalQuantity
      if (sortOrder === "asc") {
        data.sort((a, b) => a.totalQuantity - b.totalQuantity);
      } else if (sortOrder === "desc") {
        data.sort((a, b) => b.totalQuantity - a.totalQuantity);
      }

      setAllStock(data);
      setLoading(false);
    });

    axiosInstance.get(`products/allproducts`).then((data) => {
      setProducts(data.data.data);
    });

    axiosInstance.get(`warehouse`).then((res) => {
      setWareHouses(res.data.data);
    });
  }, [dataChanged, searchTerm, sortOrder]);

  const productOptions = products?.map((i) => ({
    value: i._id,
    label: `${i.productName} - ${i.sku}`,
  }));

  const productHandler = (options) => {
    if (options && options.value) {
      setProdID(options.value);
    } else {
      setProdID(null);
    }
  };

  return (
    <div className={classes.pageContainer}>
      <HeaderComponent
        productHandler={productHandler}
        productOptions={productOptions}
        products={products}
        setLoading={setLoading}
        prodID={prodID}
        setSearchTerm={setSearchTerm}
        setDataChanged={setDataChanged}
        clearFilters={clearFilters}
        setClearFilters={setClearFilters}
        searchTerm={searchTerm}
        setSortOrder={setSortOrder}
      />

      <div className={classes.tableContainer}>
        <StockTable
          allStock={allStock}
          loading={loading}
          dataChanged={dataChanged}
          setDataChanged={setDataChanged}
          warehouses={warehouses}
        />
      </div>
    </div>
  );
};

export default memo(StockControl);
